import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Text } from '../typography';
import styles from './Navbar.module.scss';
import pjson from '../../../package.json';
import logo from '../../assets/logo_grad.svg';
import { Icon } from '@mui/material';
import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { navigationExpandedVar } from '../../graphql/cache/navbar';
import { animated } from 'react-spring';
import NotificationList from '../notifications/NotificationList';
import NotificationIcon from '../notifications/NotificationIcon';
import { loader } from 'graphql.macro';
import { pollInterval } from '../../constants/misc';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import WorkspaceSelector from '../WorkspaceSelector';
import { useWorkspace } from '../../providers/WorkspaceProvider';
import { useOnlineStatus } from '../../hooks/offline-hooks/offline-misc';
import { hideToast, showToast, toastVar } from '../../graphql/cache/modal';
import Toast from '../modals/Toast';
import moment from 'moment';
import Modal, { MODAL_CLOSE_EVENT } from '../modals/Modal';
import RichTextEditor from '../rich_text/RichTextEditor';
import { useCurrentUser } from '../../providers/UserProvider';
import { closeModalComponents, customColors } from '../../utilities';
import { useIsMobile } from '../../hooks/misc';
import { getRoute, paths } from '../../constants/strings';
import { useTranslation } from 'react-i18next';
import { keys } from '../../utilities/translator/translation_keys';
import InfoMenu from './InfoMenu';
import { useFlags } from 'launchdarkly-react-client-sdk';
import VectorResponseModal from '../modals/provider_modals/VectorResponseModal';
import GlobalSearch from '../../pages/dashboard/GlobalSearch/GlobalSearch';
import { defaultWorkspaceLogo } from '../../utilities';
import { useApolloContext } from '../../graphql/ApiProvider';
import { useTemplates } from '../../providers/TemplateProvider';
import { useCompanyMutations } from '../../hooks/company/update_company';
import { checkForServiceWorkerUpdate } from '../../serviceWorkerRegistration';
import { useWorkspacePermissions } from '../../providers/WorkspacePermissionsProvider';
import OfflineBanner from './OfflineBanner';
import NewVersionBanner from './NewVersionBanner';
import SimpleFileCard from '../files/SimpleFileCard';
import AnnouncementSignaturePad from '../AnnouncementSignaturePad';

const notifierQuery = loader('./Navbar.notifierCount.graphql');
const announcementQuery = loader('./Navbar.announcements.graphql');
const snoozeAnnouncementMutation = loader(
  './Navbar.announcements.snooze.graphql',
);
const acceptAnnouncementMutation = loader(
  './Navbar.announcements.accept.graphql',
);

export default function Navbar({ className, left }) {
  const { t } = useTranslation();
  const [showNotifications, setShowNotifications] = useState(false);
  const navigationExpanded = useReactiveVar(navigationExpandedVar);
  const toastProps = useReactiveVar(toastVar);
  const { user } = useCurrentUser();
  const today = new moment().format('yyyy-MM-DD');
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [infoMenuOpen, setInfoMenuOpen] = useState(false);
  const { enableVectorDatabase } = useFlags();
  const [show, setShow] = useState(false);
  // eslint-disable-next-line
  const [showSearch, setShowSearch] = useState(false);
  const [notifierCount, setNotifierCount] = useState(0);
  const [reloading, setReloading] = useState(false);
  const { persistor } = useApolloContext();
  const { refetchAllTemplates } = useTemplates();
  const { refetchWorkspacePermission, refetchWorkspaceVehicle } =
    useWorkspace();
  const { workspace } = useWorkspace();
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const online = useOnlineStatus();
  const workspaceRef = useRef(null);
  const notificationRef = useRef(null);
  const { updateUser } = useCompanyMutations();
  const frontVersion = useMemo(() => pjson.version, []);
  const { refetchAllUsers } = useWorkspacePermissions();

  const [snoozeAnnouncement] = useMutation(snoozeAnnouncementMutation, {
    refetchQueries: ['GetAnnouncementsForUser'],
  });

  const [acceptAnnouncement] = useMutation(acceptAnnouncementMutation, {
    refetchQueries: ['GetAnnouncementsForUser'],
  });

  const { refetch: refetchNotifierCount, data: { notifiersCount = 0 } = {} } =
    useQuery(notifierQuery, {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      skip: !online || !user.id,
    });

  useEffect(() => {
    setNotifierCount(notifiersCount);
  }, [notifiersCount]);

  const { data: { announcementInteractions = [] } = {} } = useQuery(
    announcementQuery,
    {
      variables: {
        userId: `${user.id}`,
        today,
      },
      skip: !user?.id || !online,
      pollInterval: pollInterval.xxl,
    },
  );

  const currentAnnouncementInteractionId = announcementInteractions[0]?.id;
  const signaturePadRef = useRef(null);
  const [hasSignature, setHasSignature] = useState(false);
  const needsSignature =
    !!announcementInteractions[0]?.announcement?.requireSignature;

  useEffect(() => {
    setHasSignature(false);
  }, [currentAnnouncementInteractionId]);

  const reload = async () => {
    if (persistor) {
      await persistor.purge();
      localStorage.setItem('lastCachePurgeTime', Date.now().toString());
    }
    await refetchAllTemplates();
    await refetchWorkspacePermission();
    await refetchWorkspaceVehicle();
    await refetchAllUsers();
  };

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.active?.postMessage({ type: 'CHECK_STORAGE' });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (
      !!online &&
      (!user.frontVersion || pjson.version !== user.frontVersion)
    ) {
      updateUser({ frontVersion: pjson.version }).then(() => {
        checkForServiceWorkerUpdate();
      });
    }
    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frontVersion, user?.frontVersion]);

  useEffect(
    () => closeModalComponents(workspaceRef, setWorkspaceOpen),
    [workspaceRef, setWorkspaceOpen],
  );
  useEffect(
    () => closeModalComponents(notificationRef, setShowNotifications),
    [notificationRef, setShowNotifications],
  );

  return (
    <div>
      <Modal
        cancelText={t(keys.announcements.SNOOZE)}
        submitText={t(keys.announcements.ACCEPT)}
        variant="success"
        title={
          announcementInteractions?.length
            ? announcementInteractions[0].announcement.title
            : ''
        }
        allowClose={false}
        submitDisabled={needsSignature && !hasSignature}
        onSubmit={async () => {
          const signatureUrl = needsSignature
            ? await signaturePadRef.current?.getSignatureUrl()
            : undefined;
          acceptAnnouncement({
            variables: {
              id: announcementInteractions[0].id,
              signatureUrl,
            },
          });
        }}
        onClose={(_, event) => {
          if (event === MODAL_CLOSE_EVENT.submitted) {
            return;
          }
          snoozeAnnouncement({
            variables: {
              id: announcementInteractions[0].id,
              dateReleased: moment().add(1, 'days').format('yyyy-MM-DD'),
            },
          }).then(() => {
            showToast({
              title: t(keys.announcements.ANNOUNCEMENT_SNOOZED),
              message: t(keys.announcements.SNOOZED_MESSAGE),
            });
          });
        }}
        open={!!announcementInteractions?.length}
      >
        {!!announcementInteractions?.length && (
          <RichTextEditor
            readOnly
            onChange={() => {}}
            id={announcementInteractions[0].announcement.id}
            value={announcementInteractions[0].announcement.content}
          />
        )}
        <div className={styles.fileList}>
          {announcementInteractions[0]?.announcement?.files?.map((f, i) => (
            <SimpleFileCard
              {...f}
              fileName={f?.name}
              file={f}
              key={`${f?.id}-file-${i}`}
            />
          ))}
        </div>
        {needsSignature && (
          <AnnouncementSignaturePad
            key={currentAnnouncementInteractionId}
            ref={signaturePadRef}
            onSignedChange={setHasSignature}
          />
        )}
      </Modal>
      <animated.div
        style={{
          paddingLeft: isMobile ? null : left,
          width: '100%',
        }}
        className={classNames(styles.container, className)}
      >
        <div className={styles.leftContainer}>
          <img
            alt="Opas Mobile"
            src={logo}
            className={styles.mobileLogo}
            onClick={() => navigate(getRoute(workspace?.id, paths.dashboard))}
          />
          <div className={styles.workspaceDropdown} ref={workspaceRef}>
            <div
              className={styles.selector}
              onClick={() => setWorkspaceOpen(!workspaceOpen)}
            >
              {!isMobile && (
                <img
                  src={workspace?.logoUrl || defaultWorkspaceLogo}
                  className={styles.avatar}
                  alt={workspace?.title}
                />
              )}
              <Text
                noSelect
                size="sm"
                weight="bold"
                className={styles.workspaceText}
                truncate
                noMargin
              >
                {workspace?.title}
              </Text>
              <Icon>{workspaceOpen ? 'expand_less' : 'expand_more'}</Icon>
            </div>
            <div className={styles.workspaceSelector}>
              <WorkspaceSelector
                show={workspaceOpen}
                setShow={setWorkspaceOpen}
              />
            </div>
          </div>
        </div>
        <div className={styles.rightContainer}>
          {process.env.REACT_APP_RELEASE_STAGE === 'production' ? null : (
            <Text
              className={styles.ghost}
              size="sm"
              weight="semiBold"
              color="secondary"
              noMargin
            >
              {`OPAS Mobile Beta v${pjson.version} (${process.env.REACT_APP_RELEASE_STAGE})`}
            </Text>
          )}
          {online ? (
            <Icon
              style={{
                color: customColors.BLUE,
                fontSize: '1.2rem',
                cursor: 'pointer',
              }}
              onClick={async () => {
                setReloading(true);
                await reload();
                setReloading(false);
                if (!reloading) {
                  showToast({
                    title: t(keys.utilities.DATA_REFETCHED),
                    message: t(keys.utilities.LATEST_DATA),
                  });
                }
              }}
            >
              refresh
            </Icon>
          ) : (
            <Icon
              className={styles.offlineIcon}
              baseClassName="material-icons-outlined"
            >
              wifi_off
            </Icon>
          )}
          <div className={styles.searchbar}>
            <GlobalSearch />
          </div>
          {enableVectorDatabase && online && (
            <div
              className={styles.vector}
              onClick={() => {
                setShow(!show);
              }}
            >
              <Icon
                baseClassName="material-icons-outlined"
                className={styles.vectorIcon}
              >
                auto_fix_high
              </Icon>
            </div>
          )}
          <NotificationList
            containerRef={notificationRef}
            show={showNotifications}
            refetchCount={refetchNotifierCount}
            onHide={() => setShowNotifications(false)}
          />
          <NotificationIcon
            count={notifierCount}
            onClick={() => setShowNotifications(!showNotifications)}
          />
          <Icon
            className={styles.helpButton}
            baseClassName="material-icons-outlined"
            onClick={() => setInfoMenuOpen(!infoMenuOpen)}
          >
            info
          </Icon>
          <InfoMenu
            show={infoMenuOpen}
            className={styles.infoMenu}
            setShow={setInfoMenuOpen}
          />
          <Toast {...toastProps} onHide={() => hideToast()} />
          <div className={styles.menuButton}>
            <Icon onClick={() => navigationExpandedVar(!navigationExpanded)}>
              {navigationExpanded ? 'expand_less' : 'menu'}
            </Icon>
          </div>
        </div>
      </animated.div>
      {show ? <VectorResponseModal setShow={setShow} show={show} /> : null}
      {showSearch && (
        <div className={styles.searchContainer}>
          <GlobalSearch />
        </div>
      )}
      <OfflineBanner />
      <NewVersionBanner />
    </div>
  );
}
